import { useState } from "react";

import { useFormikWithoutDirtyForm } from "@shared";
import { endOfISOWeek, startOfISOWeek } from "date-fns";

import { ALL_COUNSELORS_OPTION_ID } from "@/common/components/counselor-select/FormikCounselorSelect";
import { ALL_COURTS_OPTION_ID } from "@/common/components/court-select/FormikCourtSelect";
import { toOptionalId } from "@/common/utils/allOption";
import { useTranslation } from "@/translations";

import { CourtHearingsFilterValues, CourtHearingsGrouping, CourtHearingsSearchParams } from "../CourtHearings.types";
import { courtHearingsFilterValidationSchema } from "../components/filter/CourtHearingsFilterBar.utils";
import { useCourtHearingsQuery } from "./useCourtHearingsQuery";

export function useCourtHearings() {
  const { t } = useTranslation();
  // The current calendar week is what staff look at first; captured once so the default does not move
  // while the page is open.
  const [defaultPeriod] = useState(() => {
    const today = new Date();
    return { from: startOfISOWeek(today), to: endOfISOWeek(today) };
  });
  const [grouping, setGrouping] = useState<CourtHearingsGrouping>("byDate");

  const validationSchema = courtHearingsFilterValidationSchema(t);
  const formik = useFormikWithoutDirtyForm<CourtHearingsFilterValues>({
    initialValues: {
      from: defaultPeriod.from,
      to: defaultPeriod.to,
      courtId: ALL_COURTS_OPTION_ID,
      counselorId: ALL_COUNSELORS_OPTION_ID,
    },
    validationSchema,
    // The filters apply as they change; there is no explicit search step.
    onSubmit: () => undefined,
  });

  // An invalid period fetches nothing: the field errors already say why, and a report for a half-typed
  // period would only flicker.
  const { from, to, courtId, counselorId } = formik.values;
  const searchParams: CourtHearingsSearchParams | null =
    from !== null && to !== null && validationSchema.isValidSync(formik.values)
      ? {
          from,
          to,
          courtId: toOptionalId(courtId, ALL_COURTS_OPTION_ID),
          counselorId: toOptionalId(counselorId, ALL_COUNSELORS_OPTION_ID),
        }
      : null;

  const { data, isLoading, isError } = useCourtHearingsQuery(searchParams);

  return {
    formik,
    grouping,
    setGrouping,
    rows: data ?? [],
    isLoading,
    isError,
    hasSearchParams: searchParams !== null,
  };
}
